import React from 'react';

import { useGetUser, usePatchUser } from '@vlabs/api/artemy/client';
import { EditIcon } from '@vlabs/icons';
import { useHasPermission } from '@vlabs/permissions';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { toast } from 'react-toastify';

import { UserForm } from '@vlabs/monokit/entities/users/user-form';
import type { UserFormValues } from '@vlabs/monokit/entities/users/user-form';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useEditUserTranslation } from './i18n';
import type { EditUser } from './types';

type EditUserButtonProps = EditUser & Omit<ButtonProps, 'onClick'>;

const displayName = 'EditUserButton';
const testid = buildTestid(displayName);

function EditUserButton({
  userId,
  'data-testid': dataTestId = undefined,
  ...props
}: EditUserButtonProps): JSX.Element | null {
  const hasAccess = useHasPermission('users', 'update');
  const { t } = useEditUserTranslation();
  const modal = useModal({ size: 'small' });

  const { data: userData } = useGetUser(userId);

  const editUser = usePatchUser({
    mutation: {
      onSuccess: () => {
        toast.success(t('toast_success'));
        modal.close();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const handleSubmit = async (values: UserFormValues): Promise<void> => {
    await editUser.mutateAsync({ userId, data: values });
  };

  if (!hasAccess) return null;

  return (
    <React.Fragment>
      {modal.wrap(
        <UserForm
          context="update"
          defaultValues={{
            email: userData?.email,
            full_name: userData?.full_name,
            role_id: userData?.role_id ?? undefined,
          }}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<EditIcon />}
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {t('button')}
      </Button>
    </React.Fragment>
  );
}

EditUserButton.displayName = displayName;

export { EditUserButton };
